import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {{ price: number, available: boolean, inventory_quantity: number, inventory_management: string|null, inventory_policy: 'deny'|'continue' }} VariantCalcData
 */

class TileCalculator extends HTMLElement {
  /** @type {Record<string, VariantCalcData>} */
  #variants = {};

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {HTMLInputElement|null} */
  #ensuredQuantityInput = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#loadVariants();
    this.#bindInputs(signal);
    this.#listenVariantChanges(signal);

    const areaInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-area-needed]'));
    if (areaInput) {
      const parsed = Math.round(Number(areaInput.value));
      areaInput.step = '1';
      areaInput.min = '1';
      areaInput.value = String(Number.isFinite(parsed) && parsed >= 1 ? parsed : 1);
    }

    this.recalculate();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#ensuredQuantityInput = null;
  }

  #loadVariants() {
    const scriptId = this.dataset.variantsScript;
    const script = scriptId ? document.getElementById(scriptId) : null;
    if (!script?.textContent) return;

    try {
      this.#variants = JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[tile-calculator] Failed to parse variants JSON', error);
      this.#variants = {};
    }
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindInputs(signal) {
    const areaInput = this.querySelector('[data-area-needed]');
    areaInput?.addEventListener('input', () => this.recalculate(), { signal });
    areaInput?.addEventListener('change', () => {
      this.#commitAreaInput();
      this.recalculate();
    }, { signal });
    this.querySelector('[data-overage]')?.addEventListener('change', () => this.recalculate(), { signal });
  }

  /**
   * Restores a usable area only after the shopper leaves the field.
   * While typing, an empty value must stay empty so 1 or 2 can be replaced.
   */
  #commitAreaInput() {
    const areaInput = this.querySelector('[data-area-needed]');
    if (!(areaInput instanceof HTMLInputElement)) return;

    const parsed = Math.round(Number(areaInput.value));
    if (areaInput.value.trim() === '' || !Number.isFinite(parsed) || parsed < 1) {
      areaInput.value = '1';
    }
  }

  /**
   * @param {AbortSignal} signal
   */
  #listenVariantChanges(signal) {
    const scope = this.closest('.shopify-section, dialog') || document;

    scope.addEventListener(
      StandardEvents.productSelect,
      (event) => {
        const productSelectEvent = /** @type {import('@shopify/events').ProductSelectEvent} */ (event);
        if (!(productSelectEvent.target instanceof Element)) return;
        if (productSelectEvent.target.closest('product-card')) return;

        productSelectEvent.promise
          .then(({ detail }) => {
            if (!detail) return;
            if (detail.productId && detail.productId !== this.dataset.productId) return;

            const resource = detail.resource;
            if (resource && typeof resource === 'object' && 'id' in resource) {
              const id = String(/** @type {{id: number|string}} */ (resource).id);
              const price = Number(/** @type {{price?: number}} */ (resource).price);
              const available = Boolean(/** @type {{available?: boolean}} */ (resource).available);
              const inventory =
                Number(/** @type {{inventory_quantity?: number}} */ (resource).inventory_quantity) || 0;
              const management =
                /** @type {{inventory_management?: string|null}} */ (resource).inventory_management ?? null;
              const known = this.#variants[id];
              const policyRaw =
                /** @type {{inventory_policy?: string}} */ (resource).inventory_policy ??
                known?.inventory_policy ??
                'deny';
              const policy = policyRaw === 'continue' ? 'continue' : 'deny';

              if (!Number.isNaN(price)) {
                this.#variants[id] = {
                  price,
                  available,
                  inventory_quantity: inventory,
                  inventory_management: management,
                  inventory_policy: policy,
                };
                this.dataset.variantId = id;
                this.dataset.pricePerM2 = String(price);
                this.dataset.available = String(available);
                this.dataset.inventory = String(inventory);
                this.dataset.inventoryManaged = management ? 'true' : 'false';
                this.dataset.inventoryPolicy = policy;
                this.recalculate();
                return;
              }
            }

            const form = this.#getProductForm();
            const idInput = form?.querySelector('input[name="id"], select[name="id"]');
            if (idInput instanceof HTMLInputElement || idInput instanceof HTMLSelectElement) {
              this.#applyVariantId(idInput.value);
            }
          })
          .catch((error) => {
            if (error?.name !== 'AbortError') {
              console.warn('[tile-calculator] productSelect rejected', error);
            }
          });
      },
      { signal }
    );

    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
        if (target.name !== 'id') return;
        const form = this.#getProductForm();
        if (!form || !form.contains(target)) return;
        this.#applyVariantId(target.value);
      },
      { signal }
    );
  }

  /**
   * @returns {HTMLFormElement|null}
   */
  #getProductForm() {
    const section = this.closest('.shopify-section');
    const productId = this.dataset.productId;

    /** @type {ParentNode} */
    const root = section || document;

    const forms = root.querySelectorAll('product-form-component form[data-type="add-to-cart-form"], form[data-type="add-to-cart-form"]');
    for (const form of forms) {
      if (!(form instanceof HTMLFormElement)) continue;
      if (form.closest('cart-items-component, .cart-items, quick-add-modal')) continue;

      const host = form.closest('product-form-component');
      if (productId && host instanceof HTMLElement && host.dataset.productId && host.dataset.productId !== productId) {
        continue;
      }
      return form;
    }

    return null;
  }

  /**
   * @returns {HTMLInputElement|null}
   */
  #ensureQuantityInput() {
    const form = this.#getProductForm();
    if (!form) return null;

    let qty = form.querySelector('input[name="quantity"]');
    if (qty instanceof HTMLInputElement) {
      this.#ensuredQuantityInput = qty;
      return qty;
    }

    if (this.#ensuredQuantityInput?.isConnected && form.contains(this.#ensuredQuantityInput)) {
      return this.#ensuredQuantityInput;
    }

    qty = document.createElement('input');
    qty.type = 'hidden';
    qty.name = 'quantity';
    qty.value = '1';
    qty.setAttribute('data-tile-calc-quantity', 'true');
    form.appendChild(qty);
    this.#ensuredQuantityInput = qty;
    return qty;
  }

  /**
   * @param {string} variantId
   */
  #applyVariantId(variantId) {
    const data = this.#variants[variantId];
    if (!data) return;

    this.dataset.variantId = variantId;
    this.dataset.pricePerM2 = String(data.price);
    this.dataset.available = String(data.available);
    this.dataset.inventory = String(data.inventory_quantity || 0);
    this.dataset.inventoryManaged = data.inventory_management ? 'true' : 'false';
    this.dataset.inventoryPolicy = data.inventory_policy === 'continue' ? 'continue' : 'deny';
    this.recalculate();
  }

  recalculate() {
    const MIN_AREA_M2 = 3;
    const areaPerBox = Math.max(Number(this.dataset.areaPerBox) || 0, 0.0001);
    const tilesPerBox = Math.max(Number(this.dataset.tilesPerBox) || 1, 1);
    // Shopify variant.price is per m²
    const pricePerM2 = Math.max(
      Number(this.dataset.pricePerM2) || Number(this.dataset.boxPrice) || 0,
      0
    );
    // Round to cents so box × count matches the tip (avoids float drift)
    const boxPrice = Math.round(pricePerM2 * areaPerBox);
    const inventory = Number(this.dataset.inventory) || 0;
    const inventoryManaged = this.dataset.inventoryManaged === 'true';
    const inventoryPolicy = this.dataset.inventoryPolicy === 'continue' ? 'continue' : 'deny';
    const available = this.dataset.available === 'true';
    // Honor Shopify "Continue selling when out of stock" — only cap when policy is deny
    const stockLimited = inventoryManaged && inventoryPolicy === 'deny' && inventory > 0;

    const areaInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-area-needed]'));
    const overageSelect = /** @type {HTMLSelectElement|null} */ (this.querySelector('[data-overage]'));

    const overage = Number(overageSelect?.value);
    const overageRate = Number.isFinite(overage) ? overage : 0.15;

    // Max whole m² so boxes after overage stay within stock (inventory counted in boxes)
    const maxAreaFromStock = stockLimited
      ? Math.max(1, Math.floor((inventory * areaPerBox) / (1 + overageRate)))
      : Number.POSITIVE_INFINITY;

    if (areaInput) {
      areaInput.min = '1';
      areaInput.step = '1';
      if (stockLimited && Number.isFinite(maxAreaFromStock)) {
        areaInput.max = String(maxAreaFromStock);
      } else {
        areaInput.max = '999999';
      }
    }

    const rawArea = areaInput?.value ?? '';
    const typing = areaInput instanceof HTMLInputElement && document.activeElement === areaInput;
    if (typing && rawArea.trim() === '') {
      this.#showMinAreaHelper(true);
      this.#syncPurchaseGate(true);
      return;
    }

    let areaNeeded = Math.round(Number(rawArea));
    if (!Number.isFinite(areaNeeded) || areaNeeded < 1) {
      if (typing) {
        this.#showMinAreaHelper(true);
        this.#syncPurchaseGate(true);
        return;
      }
      areaNeeded = 1;
    }

    if (stockLimited && areaNeeded > maxAreaFromStock) {
      areaNeeded = maxAreaFromStock;
    }

    const typedArea = Math.round(Number(rawArea));
    const mustClamp = stockLimited && Number.isFinite(typedArea) && typedArea > maxAreaFromStock;
    if (areaInput && mustClamp && String(areaInput.value) !== String(areaNeeded)) {
      areaInput.value = String(areaNeeded);
    } else if (areaInput && !typing && String(areaInput.value) !== String(areaNeeded)) {
      areaInput.value = String(areaNeeded);
    }

    const belowMinimum = areaNeeded < MIN_AREA_M2;
    const purchaseBlocked = belowMinimum && !(stockLimited && maxAreaFromStock < MIN_AREA_M2);
    const needed = areaNeeded * (1 + overageRate);
    let boxes = Math.max(1, Math.ceil(needed / areaPerBox));
    if (stockLimited) {
      boxes = Math.min(boxes, inventory);
      boxes = Math.max(1, boxes);
    }

    const totalArea = boxes * areaPerBox;
    // Charge for whole boxes: boxes × €/box — not ceil(m²) × €/m²
    // (e.g. 18 × €100.24 = €1,804.32, not 11 × €179 = €1,969).
    const totalPrice = boxes * boxPrice;
    // Cart qty is whole m² at the Shopify €/m² price; nearest m² keeps checkout close
    // to the box-coverage total when area_per_box is fractional.
    const quantityM2 = Math.max(1, Math.round(totalArea));
    const pricePerTile = boxPrice / tilesPerBox;
    const atStockLimit = stockLimited && areaNeeded >= maxAreaFromStock;

    this.#setText('[data-boxes]', String(boxes));
    this.#setText('[data-total-area]', this.#formatNumber(totalArea));
    this.#setText('[data-tiles-per-box]', String(tilesPerBox));
    this.#setText('[data-price-per-area]', this.#money(pricePerM2));
    this.#setText('[data-price-per-tile]', this.#money(pricePerTile));
    this.#setText('[data-total-price]', this.#money(totalPrice));
    this.#setText(
      '[data-box-price-tip]',
      `${this.#money(boxPrice)} per box (${this.#money(pricePerM2)}/m²).`
    );

    const minHelper = this.querySelector('[data-min-area-helper]');
    if (minHelper instanceof HTMLElement) {
      minHelper.hidden = !belowMinimum || (stockLimited && maxAreaFromStock < MIN_AREA_M2);
    }

    const helper = this.querySelector('[data-inventory-helper]');
    if (helper instanceof HTMLElement) {
      const maxStockArea = this.#formatNumber(inventory * areaPerBox);
      helper.textContent = stockLimited
        ? `Only ${maxStockArea} m² in stock (${inventory} boxes)`
        : 'Reduce amount';
      helper.hidden = !atStockLimit;
    }

    const lead = this.querySelector('[data-lead-text]');
    if (lead instanceof HTMLElement) {
      if (available && (!stockLimited || boxes <= inventory)) {
        lead.innerHTML =
          "Available to Order. <br> Estimated delivery: 6–8 weeks.";
      } else {
        lead.textContent = this.dataset.leadTime || 'Contact for lead time';
      }
    }

    this.#syncQuantity(quantityM2);
    this.#syncLineProperties({
      boxes,
      pricePerTile,
      totalArea,
    });
    this.#syncPurchaseGate(purchaseBlocked);
  }

  /**
   * Blocks add to cart below 3 m². Variant updates call enable(), which honors this flag.
   * @param {boolean} blocked
   */
  #syncPurchaseGate(blocked) {
    const form = this.#getProductForm();
    if (form) {
      form.dataset.tileCalcPurchaseBlocked = blocked ? 'true' : 'false';
      form.querySelectorAll('add-to-cart-component').forEach((node) => {
        const canDisable = blocked && 'disable' in node && typeof node.disable === 'function';
        const canEnable =
          !blocked && this.dataset.available === 'true' && 'enable' in node && typeof node.enable === 'function';
        if (canDisable) node.disable();
        else if (canEnable) node.enable();
      });

      const accelerated = form.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      if (accelerated instanceof HTMLElement) {
        if (blocked || this.dataset.available !== 'true') accelerated.setAttribute('hidden', 'true');
        else accelerated.removeAttribute('hidden');
      }
    }

    const section = this.closest('.shopify-section');
    section?.querySelectorAll('sticky-add-to-cart [ref="addToCartButton"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = blocked || this.dataset.available !== 'true';
    });
  }

  /**
   * Syncs cart quantity in m² (Shopify price is per m²).
   * @param {number} quantityM2
   */
  #syncQuantity(quantityM2) {
    const qty = this.#ensureQuantityInput();
    if (!qty) return;

    if (String(qty.value) !== String(quantityM2)) {
      qty.value = String(quantityM2);
      qty.dispatchEvent(new Event('input', { bubbles: true }));
      qty.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Keep visible quantity selectors in sync when present
    const form = this.#getProductForm();
    if (!form) return;

    form.querySelectorAll('quantity-selector-component input[name="quantity"]').forEach((node) => {
      if (!(node instanceof HTMLInputElement) || node === qty) return;
      if (String(node.value) === String(quantityM2)) return;
      node.value = String(quantityM2);
      node.dispatchEvent(new Event('input', { bubbles: true }));
      node.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /**
   * Writes visible line-item properties for the cart drawer.
   * @param {{ boxes: number, pricePerTile: number, totalArea: number }} data
   */
  #syncLineProperties({ boxes, pricePerTile, totalArea }) {
    const form = this.#getProductForm();
    if (!form) return;

    /** @type {Record<string, string>} */
    const props = {
      Boxes: String(boxes),
      'Price per tile': this.#money(pricePerTile),
      'Total m²': this.#formatNumber(totalArea),
      _m2_per_box: this.#formatNumber(Number(this.dataset.areaPerBox) || totalArea / Math.max(boxes, 1)),
    };

    for (const [name, value] of Object.entries(props)) {
      const input = this.#ensurePropertyInput(form, name);
      if (input && input.value !== value) input.value = value;
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @returns {HTMLInputElement|null}
   */
  #ensurePropertyInput(form, name) {
    let input = form.querySelector(`input[data-tile-calc-property="${CSS.escape(name)}"]`);
    if (input instanceof HTMLInputElement) return input;

    input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.setAttribute('data-tile-calc-property', name);
    form.appendChild(input);
    return input;
  }

  /**
   * @param {boolean} visible
   */
  #showMinAreaHelper(visible) {
    const minHelper = this.querySelector('[data-min-area-helper]');
    if (minHelper instanceof HTMLElement) minHelper.hidden = !visible;
  }

  /**
   * @param {string} selector
   * @param {string} value
   */
  #setText(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }

  /**
   * @param {number} cents
   */
  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    const value = Math.round(cents);
    try {
      return formatMoney(value, format, currency);
    } catch {
      const major = (value / 100).toFixed(2);
      return format.replace(/{{\s*amount\s*}}/g, major).replace(/{{\s*currency\s*}}/g, currency);
    }
  }

  /**
   * @param {number} value
   */
  #formatNumber(value) {
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/\.?0+$/, '');
  }
}

if (!customElements.get('tile-calculator')) {
  customElements.define('tile-calculator', TileCalculator);
}
